// Temporary, local-only diagnostics. No draft, attachment, response body, or customer data.
const debugFlag = 'tradetool:generate-debug';
const logKey = 'tradetool:generate-debug-log';
const fullPageDurationMs = 5 * 60_000;
// Distinguishes concurrently open tabs and hot-reloaded copies without logging URLs.
const pageId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
let sequence = 0;
const elementIds = new WeakMap<Element, string>();
let nextElementId = 0;
// All other DOM metadata may originate from extensions or contain business data.
const safeIds = new Set(['trade-chat', 'compose-title', 'chat-input-help', 'chat-input-count', 'chat-input-error', 'next-logo']);
const safeClasses = new Set([
  'primary-button', 'primary-button-label', 'secondary-button', 'chat-attachment-actions',
  'chat-attachment-row', 'compose-band', 'chat-composer', 'workspace', 'app-shell',
  'compose-header', 'compose-help-row', 'helper-text', 'target-confirmation', 'target-confirmation-actions',
]);
const safeCursors = new Set([
  'auto', 'default', 'none', 'context-menu', 'help', 'pointer', 'progress', 'wait', 'cell',
  'crosshair', 'text', 'vertical-text', 'alias', 'copy', 'move', 'no-drop', 'not-allowed',
  'grab', 'grabbing', 'e-resize', 'n-resize', 'ne-resize', 'nw-resize', 's-resize',
  'se-resize', 'sw-resize', 'w-resize', 'ew-resize', 'ns-resize', 'nesw-resize',
  'nwse-resize', 'col-resize', 'row-resize', 'all-scroll', 'zoom-in', 'zoom-out',
]);

export function isGenerateDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  if (process.env.NODE_ENV === 'development') return true;
  try { return window.sessionStorage.getItem(debugFlag) === '1'; } catch { return false; }
}

export function logGenerateDebug(event: string, detail: Readonly<Record<string, unknown>> = {}): void {
  if (!isGenerateDebugEnabled()) return;
  const entry = { at: new Date().toISOString(), pageId, sequence: ++sequence, event, detail };
  // Diagnostics must never prevent a click or submission if console/storage is unavailable.
  try { console.info(`[generate-debug] ${JSON.stringify(entry)}`); } catch { /* best effort */ }
  try {
    const previous: unknown = JSON.parse(window.sessionStorage.getItem(logKey) ?? '[]');
    const entries = Array.isArray(previous) ? previous.slice(-199) : [];
    window.sessionStorage.setItem(logKey, JSON.stringify([...entries, entry]));
  } catch { /* Console output remains available if storage is disabled or full. */ }
}

function describeElement(value: EventTarget | null): Record<string, unknown> | null {
  if (!(value instanceof Element)) return null;
  const style = getComputedStyle(value);
  let nodeId = elementIds.get(value);
  if (!nodeId) {
    nodeId = `node-${++nextElementId}`;
    elementIds.set(value, nodeId);
  }
  const classNames = Array.from(value.classList);
  const classes = classNames.filter((name) => safeClasses.has(name));
  if (classNames.some((name) => !safeClasses.has(name))) classes.push('[redacted]');
  return {
    tag: value.tagName.toLowerCase(), nodeId,
    id: !value.id || safeIds.has(value.id) ? value.id : '[redacted]',
    classes: classes.join(' ').slice(0, 160),
    cursor: describeCursor(style.cursor), pointerEvents: style.pointerEvents, zIndex: style.zIndex,
  };
}

function describeCursor(cursor: string): string {
  return safeCursors.has(cursor) ? cursor : 'custom';
}

function containsPoint(rect: DOMRect, x: number, y: number): boolean {
  return rect.width > 0 && rect.height > 0 && x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

function describeRect(rect: DOMRect | undefined): Record<string, number> | null {
  return rect ? { x: rect.x, y: rect.y, width: rect.width, height: rect.height } : null;
}

function describeViewport(): Record<string, unknown> {
  return {
    width: innerWidth, height: innerHeight, scrollX, scrollY, dpr: devicePixelRatio,
    scale: window.visualViewport?.scale,
    offsetLeft: window.visualViewport?.offsetLeft, offsetTop: window.visualViewport?.offsetTop,
  };
}

/** Temporarily capture the entire top-level page, then return to button-scoped diagnostics.
 * Events inside iframes/browser chrome do not reach this window; focus/exit logs are clues,
 * not proof that we captured those clicks. Never prevent default or change event propagation.
 */
export function installGenerateClickDiagnostics(button: HTMLButtonElement, manual: HTMLAnchorElement | null): () => void {
  if (!isGenerateDebugEnabled()) return () => undefined;
  const expiresAt = Date.now() + fullPageDurationMs;
  let fullPageActive = true;
  let disposed = false;
  let previousHover = '';
  let lastHoverAt = -Infinity;
  let gestureStartedHere = false;
  let lastPointer: { x: number; y: number; screenX: number; screenY: number } | null = null;
  let blurTimer: number | undefined;
  const endFullPageCapture = (): void => {
    if (!fullPageActive || disposed) return;
    fullPageActive = false;
    previousHover = '';
    logGenerateDebug('diagnostics.scope.changed', { scope: 'buttons', reason: 'timeout' });
  };
  const capturePage = (): boolean => {
    // Background tabs may delay timers; enforce the deadline on the next event too.
    if (Date.now() >= expiresAt) endFullPageCapture();
    return fullPageActive;
  };
  const scopeTimer = window.setTimeout(endFullPageCapture, fullPageDurationMs);
  const observe = (event: MouseEvent): void => {
    try {
      const pageCapture = capturePage();
      if (event.type === 'pointerout' && (!pageCapture || event.relatedTarget !== null)) return;
      const { clientX: x, clientY: y } = event;
      lastPointer = { x, y, screenX: event.screenX, screenY: event.screenY };
      const rect = button.getBoundingClientRect();
      const inButton = containsPoint(rect, x, y);
      const inManual = manual ? containsPoint(manual.getBoundingClientRect(), x, y) : false;
      const targetInButton = event.target instanceof Node && button.contains(event.target);
      const targetInManual = manual && event.target instanceof Node && manual.contains(event.target);
      const relevant = inButton || inManual || targetInButton || targetInManual;
      if (!pageCapture && !relevant && !gestureStartedHere) { previousHover = ''; return; }
      const control = inButton || targetInButton ? 'generate' : inManual || targetInManual ? 'manual' : 'outside';
      const label = button.querySelector('.primary-button-label')?.getBoundingClientRect();
      const icon = button.querySelector('svg')?.getBoundingClientRect();
      const region = control !== 'generate' ? control : label && containsPoint(label, x, y) ? 'text' : icon && containsPoint(icon, x, y) ? 'icon' : 'padding';
      const target = describeElement(event.target);
      if (event.type === 'pointermove') {
        const hover = JSON.stringify({ control, region, target });
        if (hover === previousHover) return;
        // Never throttle clicks/down/up. Limit only unrelated hover noise.
        if (!relevant && Date.now() - lastHoverAt < 250) return;
        previousHover = hover;
        lastHoverAt = Date.now();
      }
      if (event.type === 'pointerdown') gestureStartedHere = Boolean(relevant);
      const hit = document.elementFromPoint(x, y);
      logGenerateDebug(`dom.${event.type}`, {
        scope: pageCapture ? 'page' : 'buttons', phase: 'window-capture',
        control, region, x, y, isTrusted: event.isTrusted, defaultPrevented: event.defaultPrevented,
        screenX: event.screenX, screenY: event.screenY, mouseButton: event.button, buttons: event.buttons,
        pointerType: 'pointerType' in event ? event.pointerType : undefined,
        hasFocus: document.hasFocus(), visibilityState: document.visibilityState,
        buttonDisabled: button.disabled, buttonCursor: describeCursor(getComputedStyle(button).cursor),
        hitButton: hit !== null && button.contains(hit), target, hit: describeElement(hit),
        path: event.composedPath().filter((node) => node instanceof Element).slice(0, 6).map(describeElement),
        stack: document.elementsFromPoint(x, y).slice(0, 5).map(describeElement),
        buttonRect: describeRect(rect), labelRect: describeRect(label), iconRect: describeRect(icon),
        viewport: describeViewport(),
      });
      if (event.type === 'pointerup' || event.type === 'pointercancel' || event.type === 'click') gestureStartedHere = false;
    } catch (error) {
      logGenerateDebug('diagnostics.error', { event: event.type, x: event.clientX, y: event.clientY, errorType: error instanceof Error ? error.name : 'UnknownError' });
    }
  };
  const observePageState = (event: Event): void => {
    if (disposed || !capturePage()) return;
    try {
      logGenerateDebug(event.type === 'visibilitychange' ? 'document.visibilitychange' : `window.${event.type}`, {
        scope: 'page', isTrusted: event.isTrusted, hasFocus: document.hasFocus(), visibilityState: document.visibilityState,
        activeElement: describeElement(document.activeElement), lastPointer,
        lastHit: lastPointer ? describeElement(document.elementFromPoint(lastPointer.x, lastPointer.y)) : null,
        buttonRect: describeRect(button.getBoundingClientRect()),
        labelRect: describeRect(button.querySelector('.primary-button-label')?.getBoundingClientRect()),
        viewport: describeViewport(),
      });
    } catch (error) {
      logGenerateDebug('diagnostics.error', { event: event.type, errorType: error instanceof Error ? error.name : 'UnknownError' });
    }
  };
  const observeBlur = (event: Event): void => {
    // activeElement may still be BODY during blur; sample after the focus transition.
    window.clearTimeout(blurTimer);
    blurTimer = window.setTimeout(() => observePageState(event), 0);
  };
  const events = ['pointermove', 'pointerdown', 'pointerup', 'pointercancel', 'mousedown', 'mouseup', 'click', 'pointerout'] as const;
  for (const name of events) window.addEventListener(name, observe, { capture: true, passive: true });
  window.addEventListener('blur', observeBlur);
  window.addEventListener('focus', observePageState);
  window.addEventListener('pagehide', observePageState);
  document.addEventListener('visibilitychange', observePageState);
  logGenerateDebug('diagnostics.ready', { version: 2, capture: 'window', scope: 'page', durationMs: fullPageDurationMs, expiresAt: new Date(expiresAt).toISOString(), buttonDisabled: button.disabled });
  return () => {
    disposed = true;
    window.clearTimeout(scopeTimer);
    window.clearTimeout(blurTimer);
    for (const name of events) window.removeEventListener(name, observe, true);
    window.removeEventListener('blur', observeBlur);
    window.removeEventListener('focus', observePageState);
    window.removeEventListener('pagehide', observePageState);
    document.removeEventListener('visibilitychange', observePageState);
    logGenerateDebug('diagnostics.stopped', { reason: 'unmount' });
  };
}
